// System
import { useSnackbar } from 'notistack';
import { AppDispatch } from 'src/app/store';
import { useFormState } from 'react-hook-form';
import { BaseSyntheticEvent, useMemo } from 'react';
// Redux
import { useSelector, useDispatch } from 'react-redux';
import { useProviderDrawer } from './useProviderDrawer';
import {
  setShowUpdateMinesProviderConfigForm,
  setShowCancelUpdateMinesProviderConfigForm,
  setSelectedMinesProviderConfig,
} from '@Modal/reducers/providers/updateMinesProviderConfigSlice/updateMinesProviderConfigSlice';

// Form
import { useUpdateMinesProviderConfigForm } from '@Forms/providers/UpdateMinesProviderConfigFormContext';

// Types
import { StoreInterface } from 'src/app/types';
// Graphql
import {
  useGetProviderConfigsQuery,
  useUpdateProviderConfigMutation,
} from '@Src/generated.graphql';
import { CurrencyInterface } from '@Modal/reducers/providers/types';

export const useUpdateMinesProviderConfig = () => {
  // Redux
  const dispatch = useDispatch<AppDispatch>();
  const { selectedProviderData } = useProviderDrawer();
  const [updateProviderConfig] = useUpdateProviderConfigMutation();
  const { refetch: refetchProviderConfigs } = useGetProviderConfigsQuery({
    variables: { id: selectedProviderData?.id },
  });

  const {
    showCancelUpdateMinesProviderConfigForm,
    showUpdateMinesProviderConfigForm,
    selectedMinesProviderConfig,
  } = useSelector((state: StoreInterface) => state?.updateMinesProviderConfig);

  // Other Hooks
  const { enqueueSnackbar } = useSnackbar();

  // Handle Form Cancels
  const handleUpdateCancel = () => {
    if (isDirty) {
      return dispatch(setShowCancelUpdateMinesProviderConfigForm(true));
    } else {
      return dispatch(setShowUpdateMinesProviderConfigForm(false));
    }
  };

  const handleCancelTakeMeBack = (action: string) => {
    if (action === 'back') {
      dispatch(setShowCancelUpdateMinesProviderConfigForm(false));
      dispatch(setShowUpdateMinesProviderConfigForm(true));
    } else {
      reset();
      dispatch(setShowCancelUpdateMinesProviderConfigForm(false));
      dispatch(setShowUpdateMinesProviderConfigForm(false));
    }
  };

  // Form Controls
  const { control, handleSubmit, reset } = useUpdateMinesProviderConfigForm();
  const { isDirty, errors } = useFormState({ control });

  const handleSubmitUpdateMinesProviderConfig = useMemo(
    () => (e?: BaseSyntheticEvent) => {
      return handleSubmit(async (formData) => {
        const convertedFormData = {
          ...formData,
          providerId: selectedProviderData?.id,
          isRoundable: formData.isRoundable == 'true',
          betSuggestions: selectedMinesProviderConfig?.betSuggestions,
          minAutoCashout: selectedMinesProviderConfig?.minAutoCashout,
          maxAutoCashout: selectedMinesProviderConfig?.maxAutoCashout,
          defaultAutoCashout: selectedMinesProviderConfig?.defaultAutoCashout,
        };
        try {
          await updateProviderConfig({
            variables: { configId: selectedMinesProviderConfig.id, input: convertedFormData },
          });

          enqueueSnackbar('Mines Provider config was successfully updated', {
            variant: 'success',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });

          // Refetch ProviderConfigs
          await refetchProviderConfigs();

          reset();
          dispatch(setShowUpdateMinesProviderConfigForm(false));
        } catch (error) {
          enqueueSnackbar(error.message, {
            variant: 'error',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });
        }
      })(e);
    },
    [updateProviderConfig]
  );

  const handleConfigRowClick = (currencyItem: CurrencyInterface) => {
    dispatch(setSelectedMinesProviderConfig(currencyItem));
    dispatch(setShowUpdateMinesProviderConfigForm(true));
  };

  return {
    // Store
    selectedMinesProviderConfig,
    showUpdateMinesProviderConfigForm,
    showCancelUpdateMinesProviderConfigForm,
    submittable: isDirty && Object.values(errors).length == 0,
    // Actions
    setSelectedMinesProviderConfig,
    setShowUpdateMinesProviderConfigForm,
    setShowCancelUpdateMinesProviderConfigForm,
    handleUpdateCancel,
    handleConfigRowClick,
    handleCancelTakeMeBack,
    handleSubmitUpdateMinesProviderConfig,
  };
};
